import React from "react";
import { motion } from "framer-motion";
import { ASSETS } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { TermLabel } from "@/components/Effects";

// Different cinematic crop per chapter — same artwork, different composition.
const CROPS = [
  "object-center aspect-[16/9]",
  "object-[30%_center] aspect-[4/3]",
  "object-[25%_20%] aspect-[4/3]",
  "object-center aspect-[16/10]",
  "object-bottom aspect-[16/9]",
];

export default function Lore() {
  const { t, lang } = useLanguage();
  const chapters = t("lore.chapters");

  return (
    <div data-testid="lore-page" className="relative">
      <PageHeader
        label={t("lore.label")}
        title={t("lore.title")}
        jpTitle={t("lore.jpTitle")}
      />

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <p className={`font-display text-xl font-bold tracking-[0.1em] text-white sm:text-2xl ${lang === "jp" ? "font-jp tracking-wide" : ""}`}>
              {t("lore.intro1")}
            </p>
            <p className="mt-5 font-mono text-xs leading-7 text-mizu-dim">
              {t("lore.intro2")}
            </p>
            <p className="mt-4 font-mono text-xs leading-7 text-mizu-silver">
              {t("lore.intro3")}
            </p>
            <div className="mt-6 font-mono text-xs tracking-[0.3em] text-mizu-cyan mizu-glow">
              OBSERVE. THINK. EXECUTE.
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mizu-corners relative overflow-hidden border border-mizu-cyan/20"
          >
            <img
              src={ASSETS.art}
              alt="MIZUKARA artwork"
              data-testid="lore-intro-art"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover object-[30%_20%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#020611]/70 via-transparent to-transparent" />
            <div className="absolute right-3 top-3 font-jp text-xs tracking-[0.4em] text-mizu-cyan/80 [writing-mode:vertical-rl]">
              自ら
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chapters */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="relative">
          {/* vertical progression line */}
          <div className="absolute bottom-0 left-[19px] top-0 hidden w-px bg-gradient-to-b from-mizu-cyan/50 via-mizu-cyan/15 to-transparent md:block" />

          {chapters.map((ch, i) => (
            <motion.article
              key={ch.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, ease: "easeOut" }}
              className="relative mb-16 md:pl-16"
              data-testid={`lore-chapter-${ch.num}`}
            >
              {/* node */}
              <div className="absolute left-3 top-2 hidden h-4 w-4 border border-mizu-cyan bg-[#020611] shadow-[0_0_12px_rgba(0,240,255,0.5)] md:block" />

              <div className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.35em] text-mizu-cyan/70">
                    CHAPTER {ch.num}
                  </div>
                  <div className="mt-1 select-none font-display text-6xl font-black text-mizu-cyan/10">
                    {ch.num}
                  </div>
                  <h3 className={`-mt-6 font-display text-2xl font-bold tracking-[0.15em] text-white ${lang === "jp" ? "font-jp tracking-widest" : ""}`}>
                    {ch.title}
                  </h3>
                  <p className="mt-4 max-w-lg font-mono text-xs leading-7 text-mizu-dim">
                    {ch.body}
                  </p>
                </div>
                <div className="mizu-corners mizu-card relative overflow-hidden border border-mizu-cyan/15">
                  <img
                    src={ASSETS.art}
                    alt={`${ch.title} — MIZUKARA`}
                    loading="lazy"
                    className={`w-full object-cover opacity-85 ${CROPS[i % CROPS.length]}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020611]/60 via-transparent to-[#020611]/30" />
                  <div className="absolute bottom-2 left-3 font-mono text-[9px] tracking-[0.3em] text-mizu-cyan/70">
                    LOG.{ch.num} // MIZUKARA
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-4 border-t border-mizu-cyan/10 pt-10 text-center"
        >
          <TermLabel className="justify-center">{t("common.byItself")}</TermLabel>
        </motion.div>
      </section>
    </div>
  );
}
